<template>
    <validation-observer v-slot="{ invalid, handleSubmit }" ref="degreeValidator">
        <form @submit.prevent="handleSubmit(submit)" @keydown="errors.clear($event.target.name)">

            <base-input class="mb-3"
                :label="$t('common.name')"
                label-classes="form-control-label required"
                :placeholder="$t('common.name')"
                v-model="value.name"
                name="name"
                rules="required"
                :error="errors.get('name')">
            </base-input>

            <base-input class="mb-3" v-if="!duplicating"
                :label="$t('common.code')"
                label-classes="form-control-label required"
                :placeholder="$t('common.code')"
                v-model="value.code"
                name="code"
                rules="required"
                :error="errors.get('code')">
            </base-input>

            <base-select class="mb-4" v-if="!duplicating"
                :label="$t('common.language')"
                :placeholder="$t('common.language')"
                label-classes="required"
                v-model="value.lang"
                :options="langs"
                @input="errors.clear('lang')"
                :error="errors.get('lang')"
                :disabled="published"
            >
            </base-select>

            <base-select class="mb-4" v-if="!duplicating"
                :label="$t('common.position') + ' 1'"
                :placeholder="$t('common.position') + ' 1'"
                label-classes="required"
                v-model="value.simulation_role_1"
                :options="simulationRolesLang('en')"
                :notes="simulationRolesLang('fr')"
                @input="errors.clear('simulation_role_1')"
                :error="errors.get('simulation_role_1')"
                :disabled="published"
            >
            </base-select>

            <base-select class="mb-4" v-if="!duplicating && value.simulation_role_1"
                :label="$t('common.competency-grid')"
                :placeholder="$t('common.competency-grid')"
                label-classes="required"
                v-model="value.grid1_id"
                :options="positionGridsLabels(1)"
                :notes="positionGridsNotes(1)"
                @input="errors.clear('grid1_id')"
                :error="errors.get('grid1_id')"
                :disabled="published"
                clearable
            >
            </base-select>

            <base-select class="mb-4" v-if="!duplicating"
                :label="$t('common.position') + ' 2'"
                :placeholder="$t('common.position') + ' 2'"
                v-model="value.simulation_role_2"
                :options="simulationRolesLang('en')"
                :notes="simulationRolesLang('fr')"
                @input="errors.clear('simulation_role_2')"
                :error="errors.get('simulation_role_2')"
                :disabled="published"
                clearable
            >
            </base-select>

            <base-select class="mb-4" v-if="!duplicating && value.simulation_role_2"
                :label="$t('common.competency-grid')"
                :placeholder="$t('common.competency-grid')"
                label-classes="required"
                v-model="value.grid2_id"
                :options="positionGridsLabels(2)"
                :notes="positionGridsNotes(2)"
                @input="errors.clear('grid2_id')"
                :error="errors.get('grid2_id')"
                :disabled="published"
                clearable
            >
            </base-select>

            <base-select class="mb-4" v-if="!duplicating && Object.keys(ratingScales).length > 1"
                :label="$t('competencies.rating-scale')"
                :placeholder="$t('competencies.rating-scale')"
                v-model="value.rating_scale"
                :options="ratingScales"
                @input="errors.clear('rating_scale')"
                :error="errors.get('rating_scale')"
                :disabled="true"
            >
            </base-select>

            <base-input class="mb-3" v-if="!duplicating"
                :label="$t('degrees.main-notification')"
                :placeholder="$t('degrees.notification-field')"
                v-model="value.main_notification"
                name="main_notification"
                :error="errors.get('main_notification')">
            </base-input>

            <base-input class="mb-3" v-if="!duplicating"
                :label="$t('degrees.gppos-notification')"
                :placeholder="$t('degrees.notification-field')"
                v-model="value.gppos_notification"
                name="gppos_notification"
                :error="errors.get('gppos_notification')">
            </base-input>

            <base-select class="mb-4" v-if="!duplicating && !$auth.user.owner_id"
                :label="$t('common.owner')"
                :placeholder="$t('common.owner')"
                v-model="value.owner_id"
                :options="owners"
                @input="errors.clear('owner_id')"
                :error="errors.get('owner_id')"
            >
            </base-select>

            <base-checkbox class="mb-3" v-if="!duplicating"
                v-model="value.shared"
            >
                {{ $t('common.shared') }}
            </base-checkbox>

            <base-select class="mb-4" v-if="!duplicating && value.status != 'active' && value.status != 'archived'"
                :label="$t('common.status')"
                :placeholder="$t('common.status')"
                v-model="value.status"
                :options="statusOptions"
                :sort="false"
            >
            </base-select>

            <div class="category form-category mb-4">
                <small><span class="text-warning">*</span> {{ $t('common.required-fields') }}</small>
            </div>

            <div class="text-center" v-if="duplicating">
                <base-button native-type="submit"
                    type="primary"
                    :disabled="errors.any() || invalid"
                >
                    {{ $t('common.duplicate') }}
                </base-button>

                <base-button type="link" @click="modal.show = false">
                    {{ $t('common.cancel') }}
                </base-button>
            </div>

            <div class="text-center" v-if="!duplicating">
                <base-button native-type="submit"
                    type="primary"
                    :disabled="errors.any() || invalid"
                >
                    {{ value.id ? $t('common.update') : $t('common.create') }}
                </base-button>

                <base-button outline 
                    type="primary"
                    :disabled="errors.any() || invalid"
                    @click="next"
                >
                    {{ $t('common.more') }}
                </base-button>
            </div>

            <div class="text-center mt-3" v-if="!duplicating">
                <base-button type="link" @click="modal.show = false">
                    {{ $t('common.cancel') }}
                </base-button>
            </div>

        </form>
    </validation-observer>
</template>

<script>
    export default {

        props: {

            value: {
                type: Object,
                required: true
            },

            errors: {
                type: Object,
                required: true
            },

            modal: {
                type: Object,
                default: null
            },

            duplicating: {
                type: Boolean,
                default: false
            },

            statusOptions: {
                type: Object,
                required: true
            },

            langs: {
                type: Object,
                required: true
            },

            simulationRoles: {
                type: Object,
                required: true
            },

            ratingScales: {
                type: Object,
                required: true
            },

            grids: {
                type: Object,
                required: true
            },

            owners: {
                type: Object,
                required: true
            },
        },

        created() {
            this.errors.setValidator('degreeValidator', this)
        },

        watch: {

            duplicating(val) {
                if (this.duplicating) {
                    this.value.name += ' (copy)'
                }
            },

            value: {
                handler(newDegree, oldDegree) {
                    // When the lang or a position change, we need to check that the selected grids are still valid.
                    // If it is not the case, then we reset the selected grids.
                    if (!this.positionGridsLabels(1)[newDegree.grid1_id]) {
                        this.value.grid1_id = null
                    }
                    if (!this.positionGridsLabels(2)[newDegree.grid2_id]) {
                        this.value.grid2_id = null
                    }
                },
                deep: true
            }
        },

        computed: {

            published() {
                return this.value.status == 'active'
            }
        },

        methods: {
            
            simulationRolesLang(lang) {
                let roles = {}
                for (let id in this.simulationRoles) {
                    roles[id] = this.simulationRoles[id][lang]
                }
                return roles
            },
            
            positionGridsLabels(position) {
                let labels = {}
                let grids = this.positionGrids(position)
                for (let gridId in grids) {
                    labels[gridId] = grids[gridId].name
                }
                return labels
            },

            positionGridsNotes(position) {
                let notes = {}
                let grids = this.positionGrids(position)
                for (let gridId in grids) {
                    notes[gridId] = grids[gridId].edition
                }
                return notes
            },

            positionGrids(position) {
                const simulationRole = this.value['simulation_role_' + position];

                const gridsSelect = {}
                for (const gridId in this.grids) {
                    const gridObject = this.grids[gridId]

                    // Not the right language.
                    if (gridObject.lang != this.value.lang) {
                        continue
                    }
                    
                    // Not the right simulation role.
                    if (gridObject.simulation_role != simulationRole && gridObject.simulation_role != 'neutral') {
                        continue
                    }
                    
                    // Keep it.
                    gridsSelect[gridId] = gridObject
                }

                // If the current simulation grid is not in the list, we must add it just to have a consistent display.
                // This may happen when a grid is removed after it has been selected in the degree.
                // Saving the form with this grid will return an error, which is right.
                /*
                const currentGrid = this.value['grid' + position];
                const currentGridId = this.value['grid' + position + '_id'];

                if (currentGridId && !gridsSelect[currentGridId]) {
                    gridsSelect[currentGridId] = currentGrid
                }
                */

                // Add the edition just after the name.
                // grids = Object.keys(grids).map(id => grids[id].name + ' ')
                return gridsSelect
            },

            submit() {
                if (this.duplicating) {
                    this.$loader.show()
                }
                this.$emit('input', { ...this.value})
            },

            next() {
                this.$emit('next')
                this.submit()
            }
        }
    }
</script>
